import { AxiosResponse } from 'axios';
import { Box, Button, CircularProgress } from '@mui/material';
import Collapsible from 'components/collapsible';
import LoadingSpinner from 'components/loading-spinner';
import TextField from 'components/text-field';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { TimerReset } from 'lucide-react';
import Mui from 'materials';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import titleCase from 'utils/title-case';
import { putCalibrationGeneralInformation } from 'views/bluviu-nutrition/features/calibrations/api/calibration';
import { useCalibrationState } from 'views/bluviu-nutrition/features/calibrations/context/calibration-context';
import { CalibrationsParams } from 'views/bluviu-nutrition/features/calibrations/types/calibrations-params.type';
import { UpdateCalibrationInformationRequest } from 'views/bluviu-nutrition/features/calibrations/types/update-calibration-information-request.type';

const PAGE_NAME_ID = 'bluViuNutrition.calibrations.editCalibration.calibrationInformation';

const SimpleCalibrationInformation = () => {
  const {
    simpleCalibrationQuery: { data, isLoading },
  } = useCalibrationState();
  const intl = useIntl();
  const params = useParams<CalibrationsParams>();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();

  const defaultValues: UpdateCalibrationInformationRequest = useMemo(() => {
    return {
      name: data?.name ?? '',
      description: data?.description ?? '',
    };
  }, [data, isLoading]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<UpdateCalibrationInformationRequest>({
    defaultValues: defaultValues,
  });

  useEffect(() => reset(defaultValues), [data]); // form is not properly defaulted unless reset is called

  const updateCalibrationInformation = usePost<AxiosResponse, UpdateCalibrationInformationRequest>(
    (request) => putCalibrationGeneralInformation(params.globalSiteId, params.calibrationId, request),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Calibration saved!' },
      });

      queryClient.invalidateQueries({
        queryKey: ['getSimpleCalibration', params.globalSiteId, params.calibrationId]
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const handleSaveCalibration = (form: UpdateCalibrationInformationRequest) => {
    if (!data) return;

    updateCalibrationInformation.mutate({
      name: form.name,
      description: form.description,
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (!data) return;

  return (
    <Collapsible label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })} open>
      <form onSubmit={handleSubmit(handleSaveCalibration)}>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <Box display={'flex'} flexDirection={'row'} gap={4}>
              <TextField
                dark
                fullWidth
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.name` })}
                controller={{
                  control,
                  name: 'name',
                }}
              />
              <TextField
                dark
                disabled
                fullWidth
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.model` })}
                value={titleCase(data.model)}
              />
            </Box>
            <TextField
              dark
              fullWidth
              multiline
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.description` })}
              controller={{
                control,
                name: 'description',
              }}
            />
          </Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box display={'flex'} alignItems={'center'}>
              <Mui.Tooltip title={'Reset inputs'}>
                <Mui.IconButton disabled={!isDirty} onClick={() => reset(defaultValues)}>
                  <TimerReset size={22} />
                </Mui.IconButton>
              </Mui.Tooltip>
            </Box>
            <Button
              disabled={updateCalibrationInformation.isPending || !isDirty}
              startIcon={updateCalibrationInformation.isPending && <CircularProgress color="inherit" size={18} />}
              type="submit">
              <FormattedMessage id={`general.save`} />
            </Button>
          </Box>
        </Box>
      </form>
    </Collapsible>
  );
};

export default React.memo(SimpleCalibrationInformation);
